import React, { useState, useEffect } from "react";
import { getSalesByItem } from "../Services/SalesReportService";
import {
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Typography,
  TextField,
  Button,
  Box,
  Container,
} from "@mui/material";
import { generatePDFReport } from "../Services/CommonService"; // Common PDF generator function
import Footer from "../Footer/Footer";

const SalesByItemReport: React.FC = () => {
  const orgId = localStorage.getItem("orgId") || "";
  const orgName = localStorage.getItem("orgName") || "My Organization";
  const orgAddress = localStorage.getItem("orgDetails") || "";

  const [selectedTimeframe, setSelectedTimeframe] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [salesData, setSalesData] = useState<any[]>([]);
  const [totalSales, setTotalSales] = useState(0);

  const fetchReport = async () => {
    try {
      const response = await getSalesByItem(
        orgId,
        selectedTimeframe,
        fromDate,
        toDate
      );
      setSalesData(response.data.salesData || []);
      setTotalSales(response.data.grandTotal.totalSalesAmount || 0);
    } catch (error) {
      console.error("Error fetching sales report by item:", error);
    }
  };

  const downloadPDF = () => {
    if (salesData.length === 0) {
      alert("No data available to generate a report.");
      return;
    }
    const columns = [
      "Sn.",
      "Item ID",
      "Item Name",
      "Total Qty Sold",
      "Total Sales Amount",
    ];
    const data = salesData.map((item, index) => [
      index + 1, // Serial Number
      item.itemId,
      item.itemDesc,
      item.totalQtySold,
      item.totalSalesAmount.toFixed(2),
    ]);
    generatePDFReport(
      "Sales Report by Item",
      orgName,
      orgAddress,
      columns,
      data,
      totalSales
    );
  };

  return (
    <>
      <div className="page-location">
        <span>Reports &gt; Sales By Item Report</span>
      </div>
      <Container maxWidth="lg" sx={{ padding: 1 }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          {/* Timeframe Label and Select Field in the Same Row */}
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={2}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              Timeframe :
            </Typography>
            <Select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="" disabled>
                Select Timeframe
              </MenuItem>
              {/* <MenuItem value="last10">Last 10 Transactions</MenuItem> */}
              <MenuItem value="lastMonth">Last 1 Month</MenuItem>
              <MenuItem value="last3Months">Last 3 Months</MenuItem>
              <MenuItem value="financialYear">Current Financial Year</MenuItem>
              <MenuItem value="dateRange">By Date Range</MenuItem>
            </Select>
          </Box>

          {/* Date Range Fields (Only When Date Range is Selected) */}
          {selectedTimeframe === "dateRange" && (
            <Box display="flex" gap={2} justifyContent="center" mt={2}>
              <TextField
                type="date"
                label="From Date"
                InputLabelProps={{ shrink: true }}
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                sx={{
                  width: 180,
                  height: 50,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "50px",
                    "& fieldset": { borderColor: "#1976d2" },
                    "&:hover fieldset": { borderColor: "#1565c0" },
                    "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                  },
                }}
              />
              <TextField
                type="date"
                label="To Date"
                InputLabelProps={{ shrink: true }}
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                sx={{
                  width: 180,
                  height: 50,
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "8px",
                    height: "50px",
                    "& fieldset": { borderColor: "#1976d2" },
                    "&:hover fieldset": { borderColor: "#1565c0" },
                    "&.Mui-focused fieldset": { borderColor: "#0d47a1" },
                  },
                }}
              />
            </Box>
          )}

          {/* Search Button Centered Below */}
          <Button variant="contained" color="primary" onClick={fetchReport}>
            Search
          </Button>
        </Box>

        {salesData.length > 0 && (
          <>
            {/* <div>Total Sales Amount: {totalSales}</div> */}
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Sn.</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Item ID</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Item Name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Total Qty Sold</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Total Sales Amount</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salesData.map((item, index) => (
                  <TableRow key={item.itemId}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item.itemId}</TableCell>
                    <TableCell>{item.itemDesc}</TableCell>
                    <TableCell>{item.totalQtySold}</TableCell>
                    <TableCell>{item.totalSalesAmount.toFixed(2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                color="secondary"
                onClick={downloadPDF}
                sx={{
                  m: 1,
                  p: 1,
                }}
              >
                Download PDF
              </Button>
            </Box>
          </>
        )}
      </Container>
      <Box
        sx={{
          position: "fixed",
          left: 0,
          bottom: 0,
          width: "100%",
        }}
      >
        <Footer />
      </Box>
    </>
  );
};

export default SalesByItemReport;
